/**
 * Pull to Refresh Component
 * 
 * Mobile-friendly pull-down-to-refresh functionality
 * 
 * Version: 6.2.0
 */
import React, { useState, useRef, useCallback } from 'react'
import './PullToRefresh.css'

interface PullToRefreshProps {
  children: React.ReactNode
  onRefresh: () => Promise<void>
  threshold?: number // Minimum distance to trigger refresh
  maxPull?: number // Maximum pull distance
  disabled?: boolean
  className?: string
}

type PullState = 'idle' | 'pulling' | 'ready' | 'refreshing' | 'complete'

const PullToRefresh: React.FC<PullToRefreshProps> = ({
  children,
  onRefresh,
  threshold = 80,
  maxPull = 120,
  disabled = false,
  className = ''
}) => {
  const [pullState, setPullState] = useState<PullState>('idle')
  const [pullDistance, setPullDistance] = useState(0)
  const startYRef = useRef(0)
  const currentYRef = useRef(0)
  const isPullingRef = useRef(false)

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (disabled) return
    
    // Check if scrolled to top
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    if (scrollTop > 0) return
    
    isPullingRef.current = true
    startYRef.current = e.touches[0].clientY
    currentYRef.current = startYRef.current
    setPullState('pulling')
  }, [disabled])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPullingRef.current || disabled) return
    
    currentYRef.current = e.touches[0].clientY
    const diff = currentYRef.current - startYRef.current
    
    // Only allow pulling down
    if (diff <= 0) {
      setPullDistance(0)
      setPullState('idle')
      return
    }
    
    // Limit max pull distance
    const limitedDiff = Math.min(diff, maxPull)
    setPullDistance(limitedDiff)
    
    if (diff >= threshold) {
      setPullState('ready')
    } else {
      setPullState('pulling')
    }
  }, [disabled, threshold, maxPull])

  const handleTouchEnd = useCallback(async () => {
    if (!isPullingRef.current) return
    isPullingRef.current = false
    
    if (pullDistance >= threshold) {
      // Trigger refresh
      setPullState('refreshing')
      setPullDistance(0)
      
      try {
        await onRefresh()
        setPullState('complete')
        
        // Show complete state briefly
        setTimeout(() => {
          setPullState('idle')
        }, 1000)
      } catch (error) {
        setPullState('idle')
      }
    } else {
      setPullState('idle')
      setPullDistance(0)
    }
  }, [pullDistance, threshold, onRefresh])

  // Calculate indicator styles
  const indicatorHeight = pullState === 'idle' ? 0 : Math.max(pullDistance, 20)
  const rotation = pullState === 'ready' ? 180 : 0
  const opacity = pullDistance / maxPull

  return (
    <div 
      className={`pull-to-refresh ${className}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull Indicator */}
      <div 
        className={`pull-indicator ${pullState}`}
        style={{ height: `${indicatorHeight}px` }}
      >
        <div className="pull-indicator-content" style={{ opacity }}>
          {pullState === 'idle' && (
            <span className="pull-hint">Pull to refresh</span>
          )}
          {pullState === 'pulling' && (
            <div className="pull-arrow" style={{ transform: `rotate(${rotation}deg)` }}>
              ↓
            </div>
          )}
          {pullState === 'ready' && (
            <div className="pull-arrow ready">
              ↓
            </div>
          )}
          {pullState === 'refreshing' && (
            <div className="pull-spinner">
              <div className="spinner-ring" />
            </div>
          )}
          {pullState === 'complete' && (
            <span className="pull-complete">✓ Updated</span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="pull-content">
        {children}
      </div>
    </div>
  )
}

export default PullToRefresh